import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, Calendar, CheckCircle, LogOut, MessageCircle } from "lucide-react";
import Header from "../components/Header";
import { publicApi } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function SubscriberDashboard() {
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const [subInfo, setSubInfo] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("lexhab_sub_token");
    const infoRaw = localStorage.getItem("lexhab_sub_info");
    
    if (!token) {
      nav("/login");
      return;
    }

    if (infoRaw) {
      try {
        const info = JSON.parse(infoRaw);
        setSubInfo(info);
      } catch (e) {}
    }

    // Fetch quota to get plan details
    publicApi.quota()
      .then(({ data }) => {
        setQuota(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [nav]);

  const handleLogout = () => {
    localStorage.removeItem("lexhab_sub_token");
    localStorage.removeItem("lexhab_sub_info");
    localStorage.removeItem("lexhab_sub_plan");
    nav("/");
  };

  const goToChat = () => {
    nav("/chat");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-4">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
            <p className="text-sm text-foreground/60">
              {locale === "fr" ? "Chargement de votre espace..." : "Loading your dashboard..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = subInfo?.expires_at && new Date(subInfo.expires_at) < new Date();
  const planName = quota?.plan || subInfo?.plan || "standard";
  const billingCycle = quota?.billing_cycle || "monthly";

  return (
    <div className="min-h-screen flex flex-col" data-testid="subscriber-dashboard">
      <Header />
      
      <div className="flex-1 max-w-5xl w-full mx-auto px-6 md:px-10 py-12">
        {/* Header section */}
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent font-semibold mb-2">
            / espace abonné
          </div>
          <h1 className="font-display text-3xl font-semibold text-primary mb-2">
            {locale === "fr" ? "Mon Compte" : "My Account"}
          </h1>
          <p className="text-sm text-foreground/60">
            {locale === "fr" 
              ? "Gérez votre abonnement et accédez à vos avantages" 
              : "Manage your subscription and access your benefits"}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Profile Card */}
          <div className="bg-white border border-primary/15 rounded-sm shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 text-primary rounded-sm flex items-center justify-center">
                <User size={20} />
              </div>
              <h2 className="font-display text-lg font-semibold text-primary">
                {locale === "fr" ? "Profil" : "Profile"}
              </h2>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Mail size={16} className="text-foreground/40 mt-0.5" />
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-wider text-foreground/50">
                    {locale === "fr" ? "EMAIL" : "EMAIL"}
                  </p>
                  <p className="text-sm text-foreground">{subInfo?.email || "-"}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Calendar size={16} className="text-foreground/40 mt-0.5" />
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-wider text-foreground/50">
                    {locale === "fr" ? "EXPIRATION" : "EXPIRATION"}
                  </p>
                  <p className={`text-sm ${isExpired ? "text-red-600" : "text-foreground"}`}>
                    {subInfo?.expires_at 
                      ? new Date(subInfo.expires_at).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
                          day: "numeric",
                          month: "long",
                          year: "numeric"
                        })
                      : locale === "fr" ? "Illimité" : "Unlimited"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Card */}
          <div className="bg-white border border-primary/15 rounded-sm shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/10 text-accent rounded-sm flex items-center justify-center">
                <CheckCircle size={20} />
              </div>
              <h2 className="font-display text-lg font-semibold text-primary">
                {locale === "fr" ? "Abonnement" : "Subscription"}
              </h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="font-mono text-[9px] uppercase tracking-wider text-foreground/50 mb-1">
                  {locale === "fr" ? "FORMULE ACTUELLE" : "CURRENT PLAN"}
                </p>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-sm uppercase tracking-wide">
                    {planName}
                  </span>
                  {billingCycle === "monthly" && (
                    <span className="text-xs text-foreground/50">
                      ({locale === "fr" ? "mensuel" : "monthly"})
                    </span>
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-primary/10">
                <p className="font-mono text-[9px] uppercase tracking-wider text-foreground/50 mb-2">
                  {locale === "fr" ? "STATUT" : "STATUS"}
                </p>
                {isExpired ? (
                  <div className="flex items-center gap-2 text-red-600">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    <span className="text-sm font-medium">
                      {locale === "fr" ? "Expiré" : "Expired"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-600">
                    <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">
                      {locale === "fr" ? "Actif" : "Active"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="md:col-span-2 bg-white border border-primary/15 rounded-sm shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 text-primary rounded-sm flex items-center justify-center">
                <MessageCircle size={20} />
              </div>
              <h2 className="font-display text-lg font-semibold text-primary">
                {locale === "fr" ? "Actions Rapides" : "Quick Actions"}
              </h2>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={goToChat}
                className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors"
              >
                <MessageCircle size={16} />
                {locale === "fr" ? "Poser une question" : "Ask a Question"}
              </button>
              
              <button
                onClick={() => nav("/pricing")}
                className="inline-flex items-center gap-2 bg-white border border-primary/20 text-primary px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-primary/5 transition-colors"
              >
                <Calendar size={16} />
                {locale === "fr" ? "Renouveler l'abonnement" : "Renew Subscription"}
              </button>
              
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 bg-white border border-primary/20 text-primary px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-primary/5 transition-colors"
              >
                <LogOut size={16} />
                {locale === "fr" ? "Se déconnecter" : "Logout"}
              </button>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-8 bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/10 rounded-sm p-6">
          <h3 className="font-display text-lg font-semibold text-primary mb-4">
            {locale === "fr" ? "Vos Avantages Premium" : "Your Premium Benefits"}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              locale === "fr" ? "Questions illimitées" : "Unlimited questions",
              locale === "fr" ? "Réponses prioritaires" : "Priority responses",
              locale === "fr" ? "Support dédié" : "Dedicated support",
              locale === "fr" ? "Accès aux nouvelles fonctionnalités" : "Early access to new features",
            ].map((benefit, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle size={16} className="text-accent" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
